import { useEffect, useId, useRef, useState } from 'react';
import type { ServiceCategory } from '@palihog/shared';
import { supabase } from '../../lib/supabase';

export interface StoreBranch {
  id: string;
  store_name: string;
  branch_name: string;
  address: string;
  latitude: number;
  longitude: number;
}

type Search = (text: string, category: ServiceCategory) => Promise<StoreBranch[]>;

async function searchDirectory(text: string, category: ServiceCategory) {
  const { data, error } = await supabase.rpc('search_store_branches', { search_text: text, requested_category: category, result_limit: 8 });
  if (error) throw error;
  return (data ?? []) as StoreBranch[];
}

export function StoreBranchCombobox({ category, onSelect, search = searchDirectory }: { category: ServiceCategory; onSelect: (branch: StoreBranch) => void; search?: Search }) {
  const listId = useId();
  const request = useRef(0);
  const [value, setValue] = useState('');
  const [items, setItems] = useState<StoreBranch[]>([]);
  const [active, setActive] = useState(-1);
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');

  useEffect(() => {
    const query = value.trim();
    const sequence = ++request.current;
    if (query.length < 2) { setItems([]); setStatus('idle'); return; }
    setStatus('loading');
    const timer = window.setTimeout(() => void search(query, category).then((results) => {
      if (sequence !== request.current) return;
      setItems(results); setActive(results.length ? 0 : -1); setStatus('idle');
    }).catch(() => { if (sequence === request.current) { setItems([]); setStatus('error'); } }), 250);
    return () => window.clearTimeout(timer);
  }, [value, category, search]);

  const choose = (item: StoreBranch) => { onSelect(item); setValue(''); setItems([]); setActive(-1); };
  const open = value.trim().length >= 2 && (status !== 'idle' || items.length > 0);
  return <div className="combobox">
    <label htmlFor={`${listId}-input`}>Search verified stores and branches</label>
    <input id={`${listId}-input`} role="combobox" autoComplete="off" value={value} aria-autocomplete="list" aria-expanded={open} aria-controls={listId} aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined} placeholder="Type at least 2 characters" onChange={(event) => setValue(event.target.value)} onKeyDown={(event) => {
      if (event.key === 'ArrowDown' && items.length) { event.preventDefault(); setActive((current) => (current + 1) % items.length); }
      if (event.key === 'ArrowUp' && items.length) { event.preventDefault(); setActive((current) => (current - 1 + items.length) % items.length); }
      if (event.key === 'Enter' && active >= 0) { event.preventDefault(); choose(items[active]!); }
      if (event.key === 'Escape') { setItems([]); setActive(-1); }
    }} />
    <div aria-live="polite" className="fine-print">{status === 'loading' ? 'Searching…' : status === 'error' ? 'Store search failed. Try again.' : value.trim().length >= 2 && !items.length ? 'No approved active branches found.' : 'Results are maintained and verified by Palihog.'}</div>
    {items.length > 0 && <ul id={listId} role="listbox" className="combobox-list">{items.map((item, index) => <li id={`${listId}-${index}`} role="option" aria-selected={index === active} className={index === active ? 'active' : ''} key={item.id} onMouseDown={(event) => { event.preventDefault(); choose(item); }}><strong>{item.store_name}{item.branch_name ? ` — ${item.branch_name}` : ''}</strong><small>{item.address}</small></li>)}</ul>}
  </div>;
}
